import React from 'react'
import { makeStyles } from '@material-ui/core'
import moment from 'moment-timezone'
import classnames from 'classnames'
import type { MessageActivity } from '@paintscout/api'
import Typography from '@paintscout/ui/components/Typography'
import { useActivityTimeline } from '../ActivityTimeline/ActivityTimelineContext'

type DivProps = JSX.IntrinsicElements['div']

export interface ActivityFooterProps extends DivProps {
  activity?: MessageActivity
  timestamp: number

  /**
   * Show the full date alongside the time, instead of just the time. Used for pinned notes,
   * which are lifted out of their day group (and its date heading) into a "Pinned" section. See #21136.
   */
  showDate?: boolean
}

const useStyles = makeStyles(
  (theme) => ({
    root: {
      marginTop: 2,
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      justifyContent: 'space-between',
      color: theme.palette.grey[500],
      paddingRight: theme.spacing()
    },
    extendedTimeDiv: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center'
    },
    sentTime: {
      textTransform: 'none'
    }
  }),
  {
    name: 'ActivityFooter'
  }
)

export default function ActivityFooter({
  activity: _activity,
  timestamp,
  showDate,
  children,
  ...props
}: ActivityFooterProps) {
  const classes = useStyles(props)
  // A timeline without day headings has no other place to show the date (ENG-2261).
  const { hideHeadings } = useActivityTimeline()
  const sentTime = moment(timestamp).format(showDate || hideHeadings ? 'MMM Do, YYYY - hh:mma' : 'hh:mma')

  const ExtendedTime = () => {
    return (
      <div className={classes.extendedTimeDiv}>
        <Typography className={classes.sentTime} variant={'overline'}>
          {sentTime}
        </Typography>
        {/* The "Read x ago" checkmark now lives in the activity subtitle on every breakpoint */}
      </div>
    )
  }

  return (
    <div {...props} className={classnames(classes.root, props.className)}>
      <ExtendedTime />
      {children}
    </div>
  )
}
